import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Smartphone } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { STAT_CARD_BASE, STAT_CHIP } from '../../components/ui/StatCard';
import SimulationActivity from '../../components/activities/SimulationActivity';
import simulationService from '../../services/simulationService';
import { showError } from '../../utils/toast';
import { deckFor, resolveRound } from '../../utils/simulationEngine';
import { toParticipantView, toParticipantReveal } from '../../utils/participantView';

/**
 * "What will my participants see?" — the trainer's window onto the phone screen.
 *
 * Every phase is selectable rather than played through, because the point is to
 * inspect each state, not to simulate a run. Rehearsal is where you play.
 */

const PHASES = [
  { key: 'brief', label: 'Brief' },
  { key: 'negotiate', label: 'Negotiate' },
  { key: 'decide', label: 'Decide' },
  { key: 'locked', label: 'Locked in' },
  { key: 'reveal', label: 'Reveal' },
  { key: 'debrief', label: 'Debrief' },
  { key: 'closed', label: 'End' },
];

const SimulationPreviewPage = () => {
  const { simulationId } = useParams();
  const navigate = useNavigate();

  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('brief');
  const [roundIndex, setRoundIndex] = useState(1);
  const [functionKey, setFunctionKey] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await simulationService.get(simulationId);
        if (!alive) return;
        if (!res.simulation.rounds.length) {
          showError('This simulation has no scenarios yet');
          navigate(`/trainer/simulations/${simulationId}`);
          return;
        }
        setSimulation(res.simulation);
        setFunctionKey(res.simulation.functions[0]?.key || null);
      } catch (err) {
        showError(err.userMessage || 'Could not load this simulation');
        navigate('/trainer/simulations/team');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [simulationId, navigate]);

  /** Sample commits so reveal/debrief have something to show — first card per team. */
  const sampleReveal = useMemo(() => {
    if (!simulation) return null;
    const round = simulation.rounds.find((r) => r.index === roundIndex);
    if (!round) return null;
    const commits = {};
    simulation.functions.forEach((fn) => {
      const deck = deckFor(simulation, fn.key);
      if (deck.length) commits[fn.key] = deck[0].id;
    });
    return resolveRound(simulation, commits, round.eventCardId);
  }, [simulation, roundIndex]);

  if (loading || !simulation || !functionKey) {
    return (
      <DashboardLayout title="Participant preview">
        <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto">
          <div className={`${STAT_CARD_BASE} animate-pulse p-8`}>
            <div className="h-5 w-1/3 rounded bg-neutral-200 dark:bg-neutral-800" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const myDeck = deckFor(simulation, functionKey);
  // Same redaction the server applies — the preview must not show more than a
  // participant would, or it stops being a preview.
  const view = toParticipantView(simulation, functionKey, { roundIndex });
  if (view && sampleReveal) {
    view.reveal = toParticipantReveal(
      simulation,
      { ...sampleReveal, roundIndex, cumulative: sampleReveal.total }
    );
  }

  return (
    <DashboardLayout title="Participant preview">
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate(`/trainer/simulations/${simulationId}`)}
              className="mb-1.5 inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            >
              <ArrowLeft size={13} strokeWidth={2} />
              Back to builder
            </button>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              <Smartphone size={17} strokeWidth={1.75} className="text-neutral-400" />
              What your participants see
            </h2>
            <p className="mt-0.5 max-w-2xl text-sm text-neutral-500 dark:text-neutral-400">
              Switch team and phase to inspect any screen. Notice that a participant never sees
              another team&apos;s options, and never sees the numbers behind a card.
            </p>
          </div>
        </div>

        {/* ---------- controls ---------- */}
        <div className={`${STAT_CARD_BASE} space-y-3 p-4`}>
          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Viewing as</p>
            <div className="flex flex-wrap gap-1.5">
              {simulation.functions.map((fn) => (
                <button
                  key={fn.key}
                  type="button"
                  onClick={() => { setFunctionKey(fn.key); setSelectedCardId(null); }}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${
                    functionKey === fn.key
                      ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                      : `${STAT_CHIP[fn.variant]} hover:opacity-80`
                  }`}
                >
                  {fn.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Phase</p>
            <div className="flex flex-wrap gap-1.5">
              {PHASES.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPhase(p.key)}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${
                    phase === p.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:ring-neutral-300 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Round</p>
              <div className="flex gap-1.5">
                {simulation.rounds.map((r) => (
                  <button
                    key={r.index}
                    type="button"
                    onClick={() => setRoundIndex(r.index)}
                    className={`h-8 w-8 rounded-lg text-xs font-semibold transition-all ${
                      roundIndex === r.index
                        ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                        : 'bg-white text-neutral-600 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                    }`}
                  >
                    {r.index}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ---------- phone ---------- */}
        <div className="flex justify-center">
          <div className="w-full max-w-[400px] overflow-hidden rounded-[2rem] border-[10px] border-neutral-800 shadow-2xl dark:border-neutral-700">
            <div className="max-h-[720px] overflow-y-auto bg-white">
              <SimulationActivity
                view={view}
                roundIndex={roundIndex}
                phase={phase}
                myFunctionKey={functionKey}
                selectedCardId={selectedCardId}
                lockedCardId={selectedCardId || myDeck[0]?.id}
                lockedCount={Math.max(1, simulation.functions.length - 1)}
                reveal={view?.reveal}
                sessionCode="4KTVY9"
                timeRemaining={phase === 'decide' || phase === 'negotiate' ? 184 : undefined}
                onSelect={setSelectedCardId}
                onLockIn={() => setPhase('locked')}
              />
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-neutral-400">
          Preview only — not connected to a live session. Phones never show the insight on a
          choice during rounds; the room hears it in the final debrief on the main screen.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default SimulationPreviewPage;
